import { FaqModal, FaqHelpFab } from '../components/FaqModal';
import { faqHelpForPath } from '../data/faqData';
import { useState, useMemo, useEffect, Suspense } from 'react';
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '@platzona/i18n';
import { countPending, subscribeConnection } from '@platzona/offline';
import { authService } from '../services/authService';
import { customerWorkspaceService } from '../services/customerWorkspaceService';
import { isOnCustomDomainHost } from '../utils/saasAppHosts';
import { SaasRouteFallback } from '../components/SaasRouteFallback';
import { IconGlobe, IconFolder, IconUsers, IconCreditCard, IconGrid, IconImage, IconSettings, IconLogOut, IconMenu, IconX, IconWand, IconShield } from '../components/Icons';
import './Layout.css';

/** Suspense here (not above Layout) so lazy page loads never tear down the shell. */
function LayoutOutlet() {
  return (
    <Suspense fallback={<SaasRouteFallback />}>
      <Outlet />
    </Suspense>
  );
}

const PublicHeader = () => {
  const { t } = useTranslation();
  return (
    <div className="layout layout--guest">
      <header className="guest-header">
        <Link to="/" className="guest-header__brand">
          <IconGlobe size={22} />
          <span>{t('page.title')}</span>
        </Link>
        <div className="guest-header__actions">
          <LanguageSelector />
          <Link to="/templates" className="guest-header__templates">{t('nav.templates', 'Browse templates')}</Link>
          <Link to="/login" className="guest-header__signin">{t('layout.signIn', 'Sign in')}</Link>
        </div>
      </header>
      <main className="guest-main">
        <LayoutOutlet />
      </main>
    </div>
  );
};

export const Layout = () => {
  const location = useLocation();
  const [faqOpen, setFaqOpen] = useState(false);
  const showFaqFab = location.pathname !== '/faq';
  const faqContext = useMemo(() => faqHelpForPath(location.pathname), [location.pathname]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = authService.getUser();
  const isCustomDomain = isOnCustomDomainHost();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [clientShellOnly, setClientShellOnly] = useState(false);
  const [pendingSync, setPendingSync] = useState(0);

  useEffect(() => {
    if (!authService.isAuthenticated()) return;
    let active = true;
    customerWorkspaceService
      .list()
      .then((res) => {
        if (!active) return;
        const workspaces = Array.isArray(res.workspaces) ? res.workspaces : [];
        const hasAny = workspaces.length > 0;
        const hasManageable = workspaces.some((workspace) => Boolean(workspace.access?.canManageWorkspace));
        setClientShellOnly(hasAny && !hasManageable);
      })
      .catch(() => {
        if (!active) return;
        setClientShellOnly(false);
      });
    return () => {
      active = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!authService.isAuthenticated()) return;
    let cancelled = false;
    const refresh = () => {
      void countPending('saas').then((n) => {
        if (!cancelled) setPendingSync(n);
      });
    };
    refresh();
    const unsub = subscribeConnection(() => refresh());
    const timer = window.setInterval(refresh, 4000);
    return () => {
      cancelled = true;
      unsub();
      window.clearInterval(timer);
    };
  }, [user?.id]);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  if (!authService.isAuthenticated()) {
    return <PublicHeader />;
  }

  return (
    <div className="layout">
      <button className="layout__mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? t('nav.closeMenu') : t('nav.openMenu')} aria-expanded={mobileOpen}>
        {mobileOpen ? <IconX size={22} /> : <IconMenu size={22} />}
      </button>

      <aside className={`layout__sidebar${mobileOpen ? ' layout__sidebar--open' : ''}`}>
        <div className="layout__brand">
          <IconGlobe size={26} className="layout__brand-icon" />
          <span>{t('page.title')}</span>
        </div>

        <nav className="layout__nav">
          {/* Bare "/" is reserved for the tenant storefront on a custom domain (see
              App.tsx's onCustomDomain branch) — it can never resolve to ProjectsPage
              there, so this link would silently leave the dashboard entirely. The
              "/customers" link right below already covers the same destination for a
              client-shell-only Kunde. */}
          {!isCustomDomain && (
            <NavLink to="/" end className={({ isActive }) => `layout__link${isActive ? ' layout__link--active' : ''}`} onClick={() => setMobileOpen(false)}>
              <IconFolder size={18} /> <span>{clientShellOnly ? t('customers.clientHome', 'My Projects') : t('nav.projects')}</span>
            </NavLink>
          )}
          <NavLink to="/customers" className={({ isActive }) => `layout__link${isActive ? ' layout__link--active' : ''}`} onClick={() => setMobileOpen(false)}>
            <IconUsers size={18} /> <span>{clientShellOnly ? t('customers.clientNav', 'My Workspace') : t('customers.nav', 'Customers')}</span>
          </NavLink>
          {!clientShellOnly && (
            <NavLink to="/orgs" className={({ isActive }) => `layout__link${isActive ? ' layout__link--active' : ''}`} onClick={() => setMobileOpen(false)}>
              <IconUsers size={18} /> <span>{t('nav.organizations')}</span>
            </NavLink>
          )}
          {!clientShellOnly && (
            <NavLink to="/templates" className={({ isActive }) => `layout__link${isActive ? ' layout__link--active' : ''}`} onClick={() => setMobileOpen(false)}>
              <IconGrid size={18} /> <span>{t('template.title')}</span>
            </NavLink>
          )}
          {!clientShellOnly && (
            <NavLink to="/media" end={false} className={({ isActive }) => `layout__link${isActive ? ' layout__link--active' : ''}`} onClick={() => setMobileOpen(false)}>
              <IconImage size={18} /> <span>{t('media.title')}</span>
            </NavLink>
          )}
          {!clientShellOnly && (
            <NavLink to="/ai" className={({ isActive }) => `layout__link${isActive ? ' layout__link--active' : ''}`} onClick={() => setMobileOpen(false)}>
              <IconWand size={18} /> <span>{t('nav.aiTools')}</span>
            </NavLink>
          )}
          {!clientShellOnly && (
            <NavLink to="/plans" className={({ isActive }) => `layout__link${isActive ? ' layout__link--active' : ''}`} onClick={() => setMobileOpen(false)}>
              <IconCreditCard size={18} /> <span>{t('nav.plans')}</span>
            </NavLink>
          )}
          {!clientShellOnly && (
            <NavLink to="/settings" className={({ isActive }) => `layout__link${isActive ? ' layout__link--active' : ''}`} onClick={() => setMobileOpen(false)}>
              <IconSettings size={18} /> <span>{t('nav.settings')}</span>
            </NavLink>
          )}
          {authService.isAdmin() && (
            <NavLink to="/admin" className={({ isActive }) => `layout__link layout__link--admin${isActive ? ' layout__link--active' : ''}`} onClick={() => setMobileOpen(false)}>
              <IconShield size={18} /> <span>{t('nav.admin')}</span>
            </NavLink>
          )}
        </nav>

        <div className="layout__user">
          <div className="layout__user-info">
            <span className="layout__username">{user?.username || 'User'}</span>
            <span className="layout__email">{user?.email || ''}</span>
            {pendingSync > 0 && (
              <span className="layout__sync-pending">
                {t('layout.syncPending', {
                  count: pendingSync,
                  defaultValue: '{{count}} pending sync',
                })}
              </span>
            )}
          </div>
          <button className="layout__logout" onClick={handleLogout} aria-label={t('nav.logout')}><IconLogOut size={18} /></button>
        </div>
      </aside>

      {mobileOpen && <div className="layout__overlay" onClick={() => setMobileOpen(false)} />}

      <main className="layout__main">
        <LayoutOutlet />
      </main>

      {showFaqFab && <FaqHelpFab onOpen={() => setFaqOpen(true)} />}
      <FaqModal open={faqOpen} onClose={() => setFaqOpen(false)} initialQuery={faqContext.initialQuery} initialItemId={faqContext.initialItemId} />
    </div>
  );
};
